import type { ResultOf, TypedDocumentNode } from '@graphql-typed-document-node/core';
import type {
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import type { ExecutableDefinitionNode } from 'graphql';
import type { ClientError, GraphQLClient, RequestDocument, Variables } from 'graphql-request';
import { generateHeaders } from '@meterup/common';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useGraphQLClient } from '../components/GraphQLClientProvider';
import { clientErrorToDisplayableError } from '../utils';

export type { TypedDocumentNode, Variables };

export function makeQueryKey<TResult, TVariables extends Variables>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables?: TVariables,
) {
  return [
    (document.definitions[0] as ExecutableDefinitionNode)?.name?.value,
    variables,
  ] satisfies QueryKey;
}

// Only using the prefix of the query so we can invalidate multiple
// server-side filtered queries at once if needed.
export function queryPrefix<TResult, TVariables extends Variables>(
  document: TypedDocumentNode<TResult, TVariables>,
) {
  return [makeQueryKey(document)[0]];
}

function variablesExtend(sourceVariables: unknown, targetVariables: unknown): boolean {
  if (sourceVariables === targetVariables) return true;

  // treat null and undefined as equivalent
  if (sourceVariables == null && targetVariables == null) return true;

  if (
    sourceVariables != null &&
    targetVariables != null &&
    typeof sourceVariables === 'object' &&
    typeof targetVariables === 'object'
  ) {
    return Object.entries(sourceVariables).every(
      ([key, sourceVal]) =>
        key in targetVariables &&
        variablesExtend(sourceVal, targetVariables[key as keyof typeof targetVariables]),
    );
  }

  return false;
}

export function queryKeyMatches(sourceKey: QueryKey, targetKey: QueryKey): boolean {
  return (
    sourceKey.length > 0 &&
    sourceKey.length <= targetKey.length &&
    sourceKey.every((sourceVal, i) => variablesExtend(sourceVal, targetKey[i]))
  );
}

export type UseGraphQLOptions<TResult> = Omit<
  UseQueryOptions<TResult, ClientError, TResult, any[]>,
  'initialData' | 'queryFn'
>;

export type UseGraphQLResult<TData> = UseQueryResult<TData, ClientError>;

export type GraphQLDocumentResult<T> = ResultOf<T>;

export type UseGraphQLMutationResult<TData, TVariables extends Variables> = UseMutationResult<
  TData,
  ClientError,
  TVariables
>;

/**
 * Executes a GraphQL query with `@tanstack/react-query`'s `useQuery` hook with the graphQL query.
 * If you want to customize the behavior use the `options` argument.
 *
 * @deprecated Use Apollo via `useSuspenseQueryPartial` instead.
 *
 * @template TResult - The type of the query result.
 * @template TVariables - The type of the query or mutation variables.
 *
 * @param {TypedDocumentNode<TResult, TVariables>} document - The GraphQL document representing the query or mutation.
 * @param {TVariables} variables - The variables to be passed to the query or mutation.
 * @param {UseGraphQLOptions} options - The options for the react-query query.
 *
 * @returns {UseQueryResult<TResult, ClientError>} - The result of the query, including fetching state, data, and errors.
 */
export function useGraphQL<TResult, TVariables extends Variables>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables?: TVariables,
  options: UseGraphQLOptions<TResult> = {},
): UseGraphQLResult<TResult> {
  const client = useGraphQLClient();
  return useQuery({
    suspense: true,
    queryKey: makeQueryKey<TResult, TVariables>(document, variables),
    ...options,
    queryFn: async ({ signal }) =>
      client
        .request<TResult>({ document, signal, variables, requestHeaders: generateHeaders() })
        .catch((error) => {
          const displayableError: Error = clientErrorToDisplayableError(error);
          throw displayableError;
        }),
  });
}

export function useGraphQLMutation<TData = unknown, TVariables extends Variables = Variables>(
  document: RequestDocument | TypedDocumentNode<TData, TVariables>,
  options?: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'>,
): UseGraphQLMutationResult<TData, TVariables> {
  const client = useGraphQLClient();
  return useMutation<TData, ClientError, TVariables>(
    async (variables: TVariables) =>
      client.request<TData>(document, variables, generateHeaders()).catch((error) => {
        const displayableError: Error = clientErrorToDisplayableError(error);
        throw displayableError;
      }),
    options,
  );
}

/**
 * Imperative version of `useGraphQL`. Only use this if you know what you're doing.
 */
export async function getGraphQLQuery<TResult, TVariables extends Variables>(
  client: GraphQLClient,
  document: TypedDocumentNode<TResult, TVariables>,
  variables?: TVariables,
): Promise<TResult> {
  return client.request<TResult>(document, variables, generateHeaders()).catch((error) => {
    const displayableError: Error = clientErrorToDisplayableError(error);
    throw displayableError;
  });
}
